
import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  noIndex?: boolean;
  schemaJson?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';
const DEFAULT_IMAGE = 'https://static.readdy.ai/image/8c75b34306599e28cafd501142ec58d6/6544712b4051e0bdfd79201f42094c9a.png';

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  schemaJson,
}: SEOHeadProps) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, true);
      setMeta('twitter:description', description);
    }
    if (keywords) setMeta('keywords', keywords);
    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow');
    }

    const canonical = `${SITE_URL}${canonicalPath}`;
    setLink('canonical', canonical);
    setMeta('og:url', canonical, true);
    if (title) {
      setMeta('og:title', title, true);
      setMeta('twitter:title', title);
    }
    setMeta('og:image', ogImage, true);
    setMeta('twitter:image', ogImage);
    setMeta('last-modified', new Date().toISOString().split('T')[0]);

    // Schema JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-schema]');
    existingScripts.forEach((s) => s.remove());

    const schemas = schemaJson
      ? Array.isArray(schemaJson)
        ? schemaJson
        : [schemaJson]
      : [];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalPath, ogImage, noIndex, schemaJson]);

  return null;
}